import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { PublicEdufadSummary, ReadingSearchResult } from "@edufadder/shared-types";
import { colors, spacing } from "@edufadder/ui";
import { HowTheseWereChosen, ReadingCard } from "../components/FurtherReading.js";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";

type State =
  | { kind: "idle" }
  | { kind: "searching"; query: string }
  | { kind: "done"; result: ReadingSearchResult }
  | { kind: "error"; message: string };

export function ResearchPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") ?? "";
  const [text, setText] = useState(initial);
  const [state, setState] = useState<State>({ kind: "idle" });
  const [edufads, setEdufads] = useState<PublicEdufadSummary[]>([]);

  useEffect(() => {
    document.title = "Find research — EduFadder";
    api.listPublicEdufads().then(setEdufads).catch(() => undefined);
    return () => {
      document.title = "EduFadder — check the evidence behind education claims";
    };
  }, []);

  useEffect(() => {
    const q = (params.get("q") ?? "").trim();
    if (q.length < 3) return;
    setText(q);
    setState({ kind: "searching", query: q });
    let live = true;
    api
      .searchResearch(q)
      .then((result) => live && setState({ kind: "done", result }))
      .catch((err) => {
        if (!live) return;
        const message =
          err instanceof ApiError && err.status === 429
            ? err.message
            : "The research catalogues couldn't be reached just now. Please try again in a moment.";
        setState({ kind: "error", message });
      });
    return () => {
      live = false;
    };
  }, [params]);

  function submit(e: FormEvent): void {
    e.preventDefault();
    const q = text.trim();
    if (q.length >= 3) setParams({ q });
  }

  const chips = edufads.filter((e) => e.search_phrase).slice(0, 14);

  return (
    <div style={{ ...s.wide, maxWidth: 920 }}>
      <h1 style={{ ...s.heading1, fontSize: 34 }}>Find research on an education idea</h1>
      <hr style={s.goldRule} />
      <p style={{ ...s.lead, maxWidth: 780 }}>
        Search open research catalogues for articles you can read for free — only peer-reviewed or journal articles and
        reviews that are among the most cited in their field. Good for going beyond a headline, a workshop slide, or a
        social-media post.
      </p>

      <form onSubmit={submit} role="search" style={{ display: "flex", gap: spacing(2), flexWrap: "wrap", margin: `${spacing(6)} 0 ${spacing(3)}` }}>
        <label htmlFor="research-q" style={{ position: "absolute", left: -9999 }}>
          Search phrase
        </label>
        <input
          id="research-q"
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. learning styles, growth mindset, retrieval practice"
          minLength={3}
          maxLength={100}
          style={{ ...s.input, flex: "1 1 320px", fontSize: 17, padding: "12px 14px" }}
        />
        <button type="submit" style={{ ...s.buttonGold, fontSize: 16 }} disabled={state.kind === "searching" || text.trim().length < 3}>
          {state.kind === "searching" ? "Searching…" : "Search"}
        </button>
      </form>

      {chips.length > 0 && (
        <div style={{ display: "flex", gap: spacing(2), flexWrap: "wrap", alignItems: "center", marginBottom: spacing(4) }}>
          <span style={s.mutedText}>Try:</span>
          {chips.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => setParams({ q: e.search_phrase ?? "" })}
              style={{ ...s.chip, cursor: "pointer", border: `1px solid ${colors.border}`, background: colors.goldTint }}
            >
              {e.search_phrase}
            </button>
          ))}
        </div>
      )}

      <p style={{ ...s.mutedText, maxWidth: 780 }}>
        Your search phrase is sent from EduFadder's server to the catalogues — never with anything about you — and isn't stored.{" "}
        <Link to="/privacy">Privacy</Link>
      </p>

      <section aria-live="polite" style={{ ...s.card, marginTop: spacing(4) }}>
        {state.kind === "idle" && <p style={s.mutedText}>Type a phrase or pick one above.</p>}
        {state.kind === "searching" && <p style={s.mutedText}>Searching OpenAlex and ERIC for “{state.query}”… this can take a few seconds.</p>}
        {state.kind === "error" && <p style={s.errorText}>{state.message}</p>}
        {state.kind === "done" && (
          <>
            <h2 style={{ ...s.heading2, color: colors.brand, marginBottom: 0 }}>
              {state.result.items.length === 0
                ? `No article met all the criteria for “${state.result.query}”`
                : `${state.result.items.length} highly cited, free-to-read article${state.result.items.length === 1 ? "" : "s"} on “${state.result.query}”`}
            </h2>
            <HowTheseWereChosen sources={state.result.sources} />
            {state.result.items.length === 0 ? (
              <p style={s.mutedText}>
                Try a shorter or more common phrase (for example “learning styles” rather than a full sentence). The bar is
                deliberately high: free to read, peer-reviewed, and in the top 10% most cited.
              </p>
            ) : (
              <ul style={{ margin: 0, padding: 0 }}>
                {state.result.items.map((item) => (
                  <ReadingCard key={item.doi ?? item.title} item={item} />
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      <p style={s.mutedText}>
        Data: <a href="https://openalex.org" target="_blank" rel="noopener noreferrer">OpenAlex</a> (open catalogue of scholarly
        works) and <a href="https://eric.ed.gov" target="_blank" rel="noopener noreferrer">ERIC</a> (U.S. Department of
        Education). For EduFadder's own curated summaries, see the <Link to="/edufads">edufads library</Link>.
      </p>
    </div>
  );
}
